// The web copy of the backdating window — `_shared/backdating.ts` is the
// authority and refuses anything outside it. This only bounds the date picker,
// so a date the server would refuse is never offered. Zero imports, so
// tests/unit/backdating.test.ts can hold the two copies together under Node.

/** How far back a payment or a package sale may be dated, in calendar months. */
export const BACKDATE_MONTHS = 2

/** `YYYY-MM-DD` minus whole calendar months, clamped to the month's last day. */
export function subtractMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const first = new Date(Date.UTC(y, m - 1 - months, 1))
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  first.setUTCDate(Math.min(d, lastDay))
  return first.toISOString().slice(0, 10)
}

/** The picker's bounds for a tenant-local `today`. */
export function backdateBounds(today: string): { min: string; max: string } {
  return { min: subtractMonths(today, BACKDATE_MONTHS), max: today }
}

/** Why a chosen date cannot be sent, or null when it can. */
export function backdateProblem(date: string, today: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Choose the date it was taken.'
  const { min, max } = backdateBounds(today)
  if (date > max) return 'The date cannot be in the future.'
  if (date < min) return `The date can be at most ${BACKDATE_MONTHS} months back.`
  return null
}

/** The body fields for a chosen date: none for today, so the server stamps the moment it records. */
export function backdateField<K extends string>(key: K, date: string, today: string): Partial<Record<K, string>> {
  return date && date !== today ? ({ [key]: date } as Record<K, string>) : {}
}
